import {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
  useMemo
} from 'react'
import { useCurrentUser } from '@cma/app'
import { usePlatformClient } from '../usePlatformClient'
import { useBrandPermissions } from './useBrandPermissions'
import { PlatformClientQuery } from '@cma/generated/graphql'

export type BrandableType = 'Account' | 'Brokerage' | 'Office' | 'Team'

export interface SelectedEntity {
  brokerageId?: string
  id: string
  name: string
  usersCount: number
  brandableType: BrandableType
  displayAddress?: string
  primaryOfficeId?: string
  type?: 'office' | 'brokerage'
  [key: string]: unknown
}

interface BrandHubContextType {
  selectedEntity: SelectedEntity
  setSelectedEntity: (entity: SelectedEntity) => void
  showAddBrandForm: boolean
  setShowAddBrandForm: (show: boolean) => void
  platformClient: PlatformClientQuery['platformClient']
  hasBrandPermissions: boolean
  hasOfficePermissionForSelected: boolean
  isSuperAdmin: boolean
  isOfficeAdmin: boolean
  navigateToRoot: () => void
}

const BrandHubContext = createContext<BrandHubContextType | undefined>(
  undefined
)

interface BrandHubProviderProps {
  children: ReactNode
}

export function BrandHubProvider({ children }: BrandHubProviderProps) {
  const { data: { currentUser } = {} } = useCurrentUser()
  const {
    hasBrandPermissions,
    hasOfficePermissionForSelected: getHasOfficePermissionForSelected,
    isSuperAdmin,
    isOfficeAdmin
  } = useBrandPermissions()
  const { data: { platformClient } = {} } = usePlatformClient({
    id: currentUser?.account?.id || ''
  })

  const [selectedEntity, setSelectedEntity] = useState<SelectedEntity>({
    id: '',
    name: '',
    usersCount: 0,
    brandableType: 'Account',
    displayAddress: ''
  })
  const [showAddBrandForm, setShowAddBrandForm] = useState(false)

  const navigateToRoot = () => {
    if (platformClient?.id) {
      setSelectedEntity({
        id: platformClient.id,
        name: platformClient.platformAccountName || '',
        usersCount: platformClient.usersCount || 0,
        brandableType: 'Account',
        displayAddress: ''
      })
    }
  }

  useEffect(() => {
    if (platformClient && (!selectedEntity.id || selectedEntity.id === '')) {
      if (platformClient.id) {
        setSelectedEntity({
          id: platformClient.id,
          name: platformClient.platformAccountName || '',
          usersCount: platformClient.usersCount || 0,
          brandableType: 'Account',
          displayAddress: ''
        })
      } else if (
        platformClient.brokerages &&
        platformClient.brokerages.length > 0
      ) {
        const firstBrokerage = platformClient.brokerages[0]
        setSelectedEntity({
          id: firstBrokerage.id,
          name: firstBrokerage.name || '',
          usersCount: firstBrokerage.usersCount || 0,
          brandableType: 'Brokerage',
          displayAddress: firstBrokerage.displayAddress || ''
        })
      } else if (platformClient.offices && platformClient.offices.length > 0) {
        const firstOffice = platformClient.offices.find(
          (office) => !office.brokerageId
        )
        if (firstOffice) {
          setSelectedEntity({
            id: firstOffice.id,
            name: firstOffice.name || '',
            usersCount: firstOffice.usersCount || 0,
            brokerageId: firstOffice.brokerageId || undefined,
            brandableType: 'Office',
            displayAddress: firstOffice.displayAddress || ''
          })
        }
      }
    }
  }, [platformClient, selectedEntity.id])

  const hasOfficePermissionForSelected = useMemo(() => {
    return getHasOfficePermissionForSelected(
      {
        id: selectedEntity.id,
        name: selectedEntity.name,
        usersCount: selectedEntity.usersCount,
        brandableType: selectedEntity.brandableType,
        type: selectedEntity.brandableType === 'Office' ? 'office' : 'brokerage'
      },
      platformClient
    )
  }, [selectedEntity, platformClient, getHasOfficePermissionForSelected])

  return (
    <BrandHubContext.Provider
      value={{
        selectedEntity,
        platformClient,
        hasBrandPermissions,
        hasOfficePermissionForSelected,
        isSuperAdmin,
        isOfficeAdmin,
        showAddBrandForm,
        setSelectedEntity,
        setShowAddBrandForm,
        navigateToRoot
      }}>
      {children}
    </BrandHubContext.Provider>
  )
}

export function useBrandHub() {
  const context = useContext(BrandHubContext)
  if (context === undefined) {
    throw new Error('useBrandHub must be used within a BrandHubProvider')
  }
  return context
}
